import { KeyRound, ShieldOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import type { ApiCredential, CredentialListQuery, CredentialOwnerType, CredentialStatus } from '../../api/areas/admin';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, FilterBar, StatCard, StatGrid, type Column } from '../../components/blocks';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText } from '../../components/States';
import { elapsedSeconds, formatDateTime, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { AccessChangesPanel, IssueApiKeyButton, RevokeKeyButton } from '../operator/AccessChanges';
import { FilterSelect, ListSection, RefreshButton, ResetButton, TextFilter, useAdminCrumbs, useParamSetter } from './ui';

const OWNER_TYPES: CredentialOwnerType[] = ['CUSTOMER', 'SYSTEM'];
const STATUSES: CredentialStatus[] = ['ACTIVE', 'REVOKED', 'EXPIRED'];
const FILTER_KEYS = ['q', 'owner_type', 'status'];
const IDLE_DAYS = 90;

const STATUS_TONE: Record<CredentialStatus, Tone> = { ACTIVE: 'ok', REVOKED: 'neutral', EXPIRED: 'warn' };

const ownerPath = (c: ApiCredential): string | undefined => (c.owner_type === 'CUSTOMER' ? `/customers/${c.owner_id}` : undefined);

/** `/admin/api-credentials`: the keys used to reach the registrar, and what each one may do. */
export function CredentialsPage() {
  const { t } = useTranslation('admin');
  useDocumentTitle(t('credentials.title'));
  const crumbs = useAdminCrumbs(t('credentials.title'));
  const [params, set, reset] = useParamSetter();
  const q: CredentialListQuery = {
    q: params.get('q') || undefined,
    owner_type: (params.get('owner_type') || undefined) as CredentialOwnerType | undefined,
    status: (params.get('status') || undefined) as CredentialStatus | undefined,
    before: params.get('before') || undefined,
    limit: 50,
  };
  const result = useApi((signal) => api.listApiCredentials(q, { signal }), [params.toString()]);
  const rows = result.status === 'success' ? result.data.items : [];
  const filtered = FILTER_KEYS.some((k) => params.get(k));
  const idle = (c: ApiCredential) => c.status === 'ACTIVE' && (!c.last_used_at || elapsedSeconds(c.last_used_at) > IDLE_DAYS * 86_400);

  const columns: Column<ApiCredential>[] = [
    {
      key: 'owner',
      header: t('credentials.col.owner'),
      // Table cells do not wrap by default, and the note under a platform component is a
      // sentence. The floor goes on the content: the table is `w-full`, so a cell width is
      // dropped on a narrow screen and the note collapses to one word per line.
      className: 'whitespace-normal',
      cell: (c) =>
        c.owner_name ? (
          <EntityRef className="min-w-48" label={c.owner_name} sub={t(`ownerType.${c.owner_type}`)} to={ownerPath(c)} id={c.owner_id} />
        ) : (
          <span className="block min-w-48" title={c.owner_id}>
            <span className="font-medium">{t('credentials.component')}</span>
            <span className="block text-xs text-muted-foreground">{t('credentials.componentHint')}</span>
          </span>
        ),
    },
    {
      key: 'key',
      header: t('credentials.col.key'),
      cell: (c) => <CopyText value={c.key_prefix} />,
      className: 'whitespace-nowrap',
    },
    {
      key: 'scopes',
      header: t('credentials.col.scopes'),
      cell: (c) =>
        c.scopes.length === 0 ? (
          <span className="text-muted-foreground">{t('credentials.noScopes')}</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {c.scopes.map((s) => (
              <Badge key={s} tone="info" icon={false}>
                {s}
              </Badge>
            ))}
          </div>
        ),
    },
    {
      key: 'created',
      header: t('credentials.col.created'),
      // Only say something about expiry when there is one; "no expiry" is the common case.
      cell: (c) => (
        <span title={formatDateTime(c.created_at)} className="whitespace-nowrap">
          {formatRelative(c.created_at)}
          {c.expires_at ? (
            <span className="block text-xs text-muted-foreground">
              {t(c.status === 'EXPIRED' ? 'credentials.expiredAt' : 'credentials.expiresAt', { when: formatRelative(c.expires_at) })}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'lastUsed',
      header: t('credentials.col.lastUsed'),
      cell: (c) =>
        c.last_used_at ? (
          <span title={formatDateTime(c.last_used_at)}>{formatRelative(c.last_used_at)}</span>
        ) : (
          <span className="text-muted-foreground">{t('credentials.neverUsed')}</span>
        ),
    },
    {
      key: 'state',
      header: t('credentials.col.state'),
      cell: (c) => (
        <span className="flex flex-col items-start gap-1">
          <Badge tone={STATUS_TONE[c.status]}>{t(`credentialStatus.${c.status}`)}</Badge>
          {c.revoked_at ? <span className="block text-xs text-muted-foreground">{t('credentials.revokedAt', { when: formatRelative(c.revoked_at) })}</span> : null}
        </span>
      ),
    },
    {
      key: 'revoke',
      header: '',
      // Platform components' keys are managed with the component, not here.
      cell: (c) => (c.status === 'ACTIVE' && c.owner_type !== 'SYSTEM' ? <RevokeKeyButton credential={c} onChanged={result.reload} /> : null),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('credentials.title')}
        crumbs={crumbs}
        subtitle={t('credentials.subtitle')}
        actions={
          <>
            <RefreshButton onClick={result.reload} />
            <IssueApiKeyButton onChanged={result.reload} />
          </>
        }
      />
      <div className="space-y-4">
        <Callout tone="warn" title={t('note.noSecrets')}>
          {t('note.keyOnce')} {t('credentials.keyHint')}
        </Callout>
        <AccessChangesPanel area="API_CREDENTIAL" reloadKey={result.status === 'success' ? result.data : undefined} onChanged={result.reload} />
        {result.status === 'success' ? (
          <StatGrid>
            <StatCard label={t('credentials.stat.active')} value={rows.filter((c) => c.status === 'ACTIVE').length} icon={KeyRound} tone="ok" />
            <StatCard
              label={t('credentials.stat.idle')}
              value={rows.filter(idle).length}
              hint={t('credentials.stat.idleHint')}
              tone={rows.some(idle) ? 'warn' : 'neutral'}
              status={rows.some(idle) ? t('credentials.neverUsed') : undefined}
            />
            <StatCard label={t('credentials.stat.revoked')} value={rows.filter((c) => c.status !== 'ACTIVE').length} icon={ShieldOff} />
            <StatCard label={t('credentials.stat.owners')} value={new Set(rows.map((c) => c.owner_id)).size} />
          </StatGrid>
        ) : null}
        <FilterBar actions={<ResetButton onClick={reset} disabled={!filtered} />}>
          <TextFilter label={t('filter.search')} value={q.q ?? ''} placeholder={t('credentials.searchPlaceholder')} onApply={(v) => set('q', v)} />
          <FilterSelect
            label={t('credentials.filterOwner')}
            value={q.owner_type ?? ''}
            onChange={(v) => set('owner_type', v)}
            options={OWNER_TYPES.map((o) => ({ value: o, label: t(`ownerType.${o}`) }))}
          />
          <FilterSelect
            label={t('credentials.filterStatus')}
            value={q.status ?? ''}
            onChange={(v) => set('status', v)}
            options={STATUSES.map((s) => ({ value: s, label: t(`credentialStatus.${s}`) }))}
          />
        </FilterBar>
        <ListSection
          result={result}
          rows={rows}
          columns={columns}
          rowKey={(c) => c.id}
          // A credential has no page of its own, and a platform component has no owner page
          // either: the row is not clickable, and the owner's name in the first column is the
          // link. A row that looks clickable but leads nowhere is worse than no affordance.
          description={result.status === 'success' ? t('credentials.count', { count: rows.length }) : undefined}
          errorTitle={t('credentials.loadError')}
          empty={t('credentials.empty')}
          nextBefore={result.status === 'success' ? result.data.next_before : null}
          onNext={(c) => set('before', c)}
          onFirst={() => set('before', '')}
          first={!q.before}
        />
      </div>
    </>
  );
}
